export default function filterRecentlyVisitedForWhitelabel(
  regions: any[],
  isWhitelabel: boolean,
  partner_id?: string,
): any[] {
  const partnerIdNum = Number(partner_id);

  return regions.filter((region) => {
    const isWhitelabelRegion = region?.is_whitelabel_region === true;
    const partnerIds = region?.whitelabel_partner_ids;

    if (isWhitelabel) {
      if (!isWhitelabelRegion) {
        return true;
      }
      return Array.isArray(partnerIds) && partnerIds.includes(partnerIdNum);
    }
    return !isWhitelabelRegion;
  });
}

export function isWhitelabelPartnerInventoryFunc(propertyData: any, partner_id?: any): any {
  if (!propertyData || !partner_id) return false;

  if (propertyData?.parent) {
    return (
      propertyData.parent?.is_whitelabel_inventory === true &&
      propertyData?.parent.whitelabel_partner_ids?.includes(partner_id)
    );
  }
  return (
    propertyData?.is_whitelabel_inventory === true &&
    propertyData?.whitelabel_partner_ids?.includes(partner_id)
  );
}

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Merge a partner's localized config overlay onto the English base config.
 *
 * `config_translations[lang]` is always a PARTIAL config. The translation service
 * returns only the fields it translated, and on a delta publish (i.e. every publish
 * after the first) s.base sends it only the changed sub-tree — so a translated
 * `footer` arrives as `{ links: [...] }` with no `is_link_enabled`.
 *
 * A one-level spread would therefore REPLACE whole objects rather than merge them,
 * and the partner loses `footer.is_link_enabled` — the flag that gates the entire
 * footer-link block — so translated locales rendered no footer links at all while
 * English was fine.
 *
 * Merge section by section instead: nested objects recurse, arrays are walked by
 * the BASE array (so a link the translation didn't cover keeps its English title,
 * and a translated `{ title }` keeps its base `link`), scalars take the translation.
 */
export function mergeLocalizedWhitelabelConfig<T extends Record<string, any>>(
  base: T,
  overlay?: Partial<T> | null,
): T {
  if (!overlay) return base;

  const merged: Record<string, any> = { ...base };

  Object.keys(overlay).forEach((key) => {
    const overlayValue = (overlay as Record<string, any>)[key];
    const baseValue = (base as Record<string, any>)[key];

    // Nothing to overlay — keep the English value.
    if (overlayValue === undefined || overlayValue === null) return;

    if (isPlainObject(overlayValue) && isPlainObject(baseValue)) {
      merged[key] = mergeLocalizedWhitelabelConfig(baseValue, overlayValue);
      return;
    }

    // Driven by the base array: the published config owns the structure, the
    // overlay only supplies translated values for the entries it covers.
    if (Array.isArray(baseValue)) {
      const overlayArray = Array.isArray(overlayValue) ? overlayValue : [];
      merged[key] = baseValue.map((baseItem, index) => {
        const overlayItem = overlayArray[index];
        if (overlayItem === undefined || overlayItem === null) return baseItem;
        return isPlainObject(baseItem) && isPlainObject(overlayItem)
          ? mergeLocalizedWhitelabelConfig(baseItem, overlayItem)
          : overlayItem;
      });
      return;
    }

    merged[key] = overlayValue;
  });

  return merged as T;
}
